import { FC, Fragment } from "react";

import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { useAxesTooltip } from "@mui/x-charts/ChartsTooltip";

import { ChartLegendDot } from "./ChartLegendDot";
import { formatTooltipAxisValue, TooltipAxisValueFormatter } from "./formatTooltipAxisValue";

import { DashboardDivider } from "@/dashboard-design-system/Divider";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

export type ChartTooltipGroup = {
  seriesIds: string[];
  totalLabel: string;
};

type ChartGroupedSeriesTooltipProps = {
  xAxisValueFormatter?: TooltipAxisValueFormatter;
  groups: ChartTooltipGroup[];
};

export const ChartGroupedSeriesTooltip: FC<ChartGroupedSeriesTooltipProps> = ({ xAxisValueFormatter, groups }) => {
  const tooltipData = useAxesTooltip()?.[0];

  if (!tooltipData) {
    return null;
  }

  const seriesItems = [...tooltipData.seriesItems].reverse();

  return (
    <Paper sx={(theme) => ({ minWidth: 400, maxWidth: 440, padding: theme.spacing(3) })}>
      <Stack gap={2}>
        <DashboardTypography variant="subtitle2" vibe="text-neutral-intense">
          {formatTooltipAxisValue(tooltipData, xAxisValueFormatter)}
        </DashboardTypography>
        {groups.map(({ seriesIds, totalLabel }) => {
          const groupItems = seriesItems.filter((series) => seriesIds.includes(series.seriesId.toString()));
          const total = groupItems.reduce((sum, series) => sum + Math.abs((series.value as number | null) ?? 0), 0);

          return (
            <Fragment key={totalLabel}>
              <DashboardDivider />
              <Stack gap={1}>
                {groupItems.map((series) => (
                  <Stack
                    key={series.seriesId}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                  >
                    <Stack direction="row" alignItems="center" gap={2} minWidth={0}>
                      <ChartLegendDot flexShrink={0} backgroundColor={series.color} />
                      <DashboardTypography variant="body2" vibe="text-neutral-quiet" noWrap>
                        {series.formattedLabel}
                      </DashboardTypography>
                    </Stack>
                    <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                      {Math.abs((series.value as number | null) ?? 0)}
                    </DashboardTypography>
                  </Stack>
                ))}
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                  <DashboardTypography variant="body2" vibe="text-neutral-quiet" fontWeight="bold">
                    {totalLabel}
                  </DashboardTypography>
                  <DashboardTypography variant="subtitle2" vibe="text-neutral-intense">
                    {total}
                  </DashboardTypography>
                </Stack>
              </Stack>
            </Fragment>
          );
        })}
      </Stack>
    </Paper>
  );
};
